import { useState } from "react";
import { CloudUpload, Download, FileSpreadsheet, Server } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { listTemplateSheets } from "../../services/exportSurveyToExcelTemplate";

export type ExportDestination = "download" | "upload";

export interface ExcelExportChoice {
  defaultSheet?: string;
  destination: ExportDestination;
}

/** Where the template comes from: a locally picked file, or the property's
 * stored excel_template_url rendered server-side by the edge function. */
export type ExcelTemplateSource = { kind: "local"; file: File } | { kind: "server" };

/**
 * Shared .xlsx/.xlsm template picker for the templated Excel exports (step 4
 * single survey, step 2 all properties). `pick()` resolves the template
 * source: when `getServerTemplateUrl` yields a URL the dialog opens in server
 * mode (no file chooser — the edge function fetches the stored template);
 * otherwise it opens the file chooser as before. The dialog then asks for the
 * destination (download vs upload to the RoboticImaging project's files) and —
 * for local multi-sheet workbooks — which sheet unmapped answers should
 * target (pre-selecting "Survey Form"). `onReady(source, choice)` fires once
 * confirmed. Render `dialog` in the tree.
 */
export function useExcelTemplatePicker(
  onReady: (source: ExcelTemplateSource, choice: ExcelExportChoice) => void,
  opts?: {
    /** Probe for the property's stored template; null/undefined → local flow. */
    getServerTemplateUrl?: () => Promise<string | null>;
    /** The template's synced default worksheet — pre-selected when the picked file has it. */
    templateDefaultSheet?: string | null;
  }
) {
  const [picker, setPicker] = useState<{
    source: ExcelTemplateSource;
    sheets: string[];
    selected: string;
    destination: ExportDestination;
  } | null>(null);

  const pickLocal = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xlsm";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      let sheets: string[];
      try {
        sheets = await listTemplateSheets(file);
      } catch (err) {
        console.error("[excel export]", err);
        alert("Failed to export: could not read the Excel file.");
        return;
      }
      // Pre-select the template's configured default worksheet, falling back
      // to a "Survey Form"-named tab, then the first sheet.
      const wanted = opts?.templateDefaultSheet?.trim().toLowerCase();
      const templateDefault = wanted
        ? sheets.find((s) => s.trim().toLowerCase() === wanted)
        : undefined;
      const surveyForm = sheets.find((s) => /survey.?form/i.test(s));
      // Keep an already-chosen destination when overriding server mode with a file.
      setPicker((prev) => ({
        source: { kind: "local", file },
        sheets,
        selected: templateDefault ?? surveyForm ?? sheets[0],
        destination: prev?.destination ?? "download",
      }));
    };
    input.click();
  };

  const pick = async () => {
    const serverUrl = await opts?.getServerTemplateUrl?.().catch(() => null);
    if (serverUrl) {
      // Server mode: the edge function reads the stored template and
      // pre-selects the Survey Form sheet itself — destination choice only.
      setPicker({ source: { kind: "server" }, sheets: [], selected: "", destination: "download" });
      return;
    }
    pickLocal();
  };

  const confirm = () => {
    if (!picker) return;
    const { source, sheets, selected, destination } = picker;
    setPicker(null);
    onReady(source, {
      defaultSheet: source.kind === "local" && sheets.length > 1 ? selected : undefined,
      destination,
    });
  };

  const dialog = (
    <Dialog open={picker !== null} onOpenChange={(open) => !open && setPicker(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Export options</DialogTitle>
          <DialogDescription>
            Choose where the export goes
            {(picker?.sheets.length ?? 0) > 1
              ? " and which sheet receives answers without a specific mapping."
              : "."}
          </DialogDescription>
        </DialogHeader>

        {picker?.source.kind === "server" && (
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Server className="h-3.5 w-3.5 shrink-0" />
              Generated on the server from the configured template.
            </p>
            {/* A picked file overrides the stored template — switches this
                dialog to the local flow. */}
            <button
              onClick={pickLocal}
              className="shrink-0 text-xs text-primary underline-offset-2 hover:underline"
            >
              Pick a file instead
            </button>
          </div>
        )}

        <div className="flex gap-2">
          {(
            [
              { key: "download", label: "Download", icon: Download },
              { key: "upload", label: "Upload to RI project", icon: CloudUpload },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setPicker((p) => (p ? { ...p, destination: key } : p))}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
                picker?.destination === key
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>
        {picker?.destination === "upload" && (
          <p className="text-xs text-muted-foreground">
            Files are added to the RoboticImaging project's Photos files (via the
            property's linked project); any previous survey export is replaced.
          </p>
        )}

        {(picker?.sheets.length ?? 0) > 1 && (
          <div className="flex flex-col gap-1">
            {picker?.sheets.map((sheet) => (
              <button
                key={sheet}
                onClick={() => setPicker((p) => (p ? { ...p, selected: sheet } : p))}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  sheet === picker.selected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                )}
              >
                <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" />
                {sheet}
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setPicker(null)}>
            Cancel
          </Button>
          <Button size="sm" onClick={confirm}>
            {picker?.destination === "upload" ? "Export & Upload" : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // pickLocal exposed for callers that want an explicit local-file override
  // (e.g. step 4's export menu) without the server-template probe.
  return { pick, pickLocal, dialog };
}
